import { GroupedTicketTypes, TicketTypeGroup, TicketTypeGroupSetting } from "../types";

type CompleteFetchingAvailableTicketTypes = {
  type: "complete_fetching_available_ticket_types";
  groupedTicketTypes: GroupedTicketTypes[];
  priority: number[];
};

type ErrorFetchingAvailableTicketTypes = {
  type: "error_fetching_available_ticket_types";
  error: string;
};

type StartAddingPromoCode = {
  type: "start_adding_promo_code";
};

type CompleteFetchingAvailableTicketTypesWithPromo = {
  type: "complete_fetching_available_ticket_types_with_promo";
  groupedTicketTypes: GroupedTicketTypes[];
  priority: number[];
};

export enum PromoStatus {
  SuccessfulPromo,
  WrongfulPromo,
  Verifying,
  NotSet,
}

export type AvailableTicketTypesReducerAction =
  | CompleteFetchingAvailableTicketTypes
  | ErrorFetchingAvailableTicketTypes
  | StartAddingPromoCode
  | CompleteFetchingAvailableTicketTypesWithPromo;

export type AvailableTicketTypesState = {
  requestState: "fetching" | "fetching_with_result" | "fetched" | "error";
  groupedTicketTypes: GroupedTicketTypes[];
};

// Define a dummy object with default values for TicketTypeGroupSetting
const defaultTicketTypeGroupSetting: TicketTypeGroupSetting = {
  ticketTypeGroupId: -1,
  eventId: 0,
  name: "",
  description: null,
  validFrom: null,
  validTo: null,
  isFullDay: false,
  ordering: 1,
  availabilityStatus: null,
};

function mapTicketTypes(ticketTypes: TicketTypeGroup[]): TicketTypeGroup[] {
  return ticketTypes.map((ticket) => ({
    ticketType: ticket.ticketType,
    ticketTypeGroupId: ticket.ticketTypeGroupId,
    ordering: ticket.ordering,
    promoted: ticket.promoted,
  }));
}

function availableTicketTypesReducer(
  state: AvailableTicketTypesState,
  action: AvailableTicketTypesReducerAction,
): AvailableTicketTypesState {
  switch (action.type) {
    case "start_adding_promo_code": {
      return {
        ...state,
        requestState: state.requestState === "fetched" ? "fetching_with_result" : "fetching",
      };
    }
    case "complete_fetching_available_ticket_types":
    case "complete_fetching_available_ticket_types_with_promo":
      const secretTicketTypes = action.groupedTicketTypes.flatMap((group) =>
        group.ticketTypes.filter((ticket) => ticket.ticketType.isSecret),
      );

      const promotedTicketTypes = action.groupedTicketTypes.flatMap((group) =>
        group.ticketTypes.filter((ticket) => ticket.promoted),
      );

      const featuredTicketTypes: TicketTypeGroup[] = action.groupedTicketTypes.flatMap((group) =>
        group.ticketTypes
          .filter((ticket) => action.priority.includes(ticket.ticketType.ticketTypeId))
          .map((ticket) => ({
            ...ticket,
            ticketType: {
              ...ticket.ticketType,
              isFeatured: true, // Setting this to true as per the logic
            },
          })),
      );

      const regularTicketTypes = action.groupedTicketTypes.flatMap((group) =>
        group.ticketTypes.filter(
          (ticket) =>
            !ticket.promoted && !ticket.ticketType.isSecret && !ticket.ticketType.isFeatured  && !action.priority.includes(ticket.ticketType.ticketTypeId),
        ),
      );

      // A bit of creative mapping here, groupId = -1 => secret, -2 => promoted, -3 => featured

      const secretGroups: GroupedTicketTypes[] = [
        {
          ticketTypeGroup: { ...defaultTicketTypeGroupSetting, ticketTypeGroupId: -1 },
          ticketTypes: mapTicketTypes(secretTicketTypes),
        },
      ];

      const promotedGroups: GroupedTicketTypes[] = [
        {
          ticketTypeGroup: { ...defaultTicketTypeGroupSetting, ticketTypeGroupId: -2 },
          ticketTypes: mapTicketTypes(promotedTicketTypes),
        },
      ];

      const featuredGroups: GroupedTicketTypes[] = [
        {
          ticketTypeGroup: { ...defaultTicketTypeGroupSetting, ticketTypeGroupId: -3 },
          ticketTypes: mapTicketTypes(featuredTicketTypes),
        },
      ];

      // If every ticketTypeGroup is null, display as one large group
      // If not, add promo and promoted as top groups
      const concatedGroups = action.groupedTicketTypes.every(
        (group) => group.ticketTypeGroup === null,
      )
        ? [
            {
              ticketTypeGroup: null,
              ticketTypes: secretTicketTypes.concat(
                featuredTicketTypes.concat(promotedTicketTypes.concat(regularTicketTypes)),
              ),
            },
          ]
        : secretGroups
            .concat(featuredGroups.concat(promotedGroups.concat(action.groupedTicketTypes)))
            .filter((group) => group.ticketTypes.length > 0);

      return {
        requestState: "fetched",
        groupedTicketTypes: concatedGroups,
      };
    case "error_fetching_available_ticket_types":
      return {
        requestState: "error",
        groupedTicketTypes: [],
      };

    default:
      throw new Error(`Unknown action: ${action}`);
  }
}

export default availableTicketTypesReducer;
